/**
 * [PURPOSE]: Item shape harmonization Sub-plan — Stone 2 (2026-05-25).
 *   Shared multi-row UI for the canonical item stat-modifications array shape:
 *   `[{ id, target, value }]`. The canonical shape originates in Sub-plan 1A
 *   (itemEffectsRegistration.js buildEffectsFromForm); Stone 4 (ItemDialog) and
 *   Stone 5 (EditItemDialog) wire this component as their stat-mod surface,
 *   converting from legacy shapes via the Stone 3 adapter.
 *
 * [WHY]: Three storage shapes coexist for item stat mods:
 *   - itemEffectsRegistration.js (canonical): `[{ id, target, value }]` array
 *   - EditItemDialog: `{ setStrength: 19, addCharisma: 2 }` object
 *   - ItemDialog: `statBonus: 'addStrength', statValue: 2` single-slot
 *   This component is the single shared surface for the canonical shape so
 *   the edit experience is consistent after both dialogs migrate (Stones 4+5).
 *
 * [API]:
 *   <StatModificationsList
 *     value={[]}                    // [{ id, target, value }, ...]
 *     onChange={(next) => void}     // emits the updated array
 *     disabled={false}
 *     placeholder="-- Stat Modification --"  // forwarded to target picker
 *   />
 *
 * [ROW SHAPE]: Each row `{ id: 'sm_...', target: 'addStrength', value: 2 }`.
 *   - `id`: stable row identity; generated via `sm_${Date.now()}_${counter}`.
 *   - `target`: a statModTarget kind id (camelCase operation+Ability string).
 *   - `value`: numeric stat value; parse logic preserves integer.
 *
 * [EMPTY ROWS]: Rows with `target === ''` are preserved during editing.
 *   Filtering out empty rows is the consumer's responsibility at save-time
 *   (the Stone 3 adapter handles that on the write path).
 *
 * [OPTGROUPS]: The target picker is a ReferencePicker with kind='statModTarget'.
 *   ReferencePicker's Stone-1 optgroup extension renders 3 optgroups
 *   (Set Stat To / Add To Stat / Subtract From Stat) matching ItemDialog's
 *   existing visual organization.
 *
 * [INTEGRATION]:
 *   - Consumed by: ItemDialog (Stone 4), EditItemDialog (Stone 5).
 *   - Imports from: composerCatalogRegistry (via ReferencePicker), NOT inline.
 *   - Tests: src/__tests__/components/composer/StatModificationsList.test.js
 */

import React from 'react';
import ReferencePicker from './ReferencePicker';

let _smCounter = 0;
function newSmId() {
  _smCounter += 1;
  return `sm_${Date.now()}_${_smCounter}`;
}

function makeDefaultRow() {
  return { id: newSmId(), target: '', value: 0 };
}

// [SUB-PLAN 1H-H-maxcap-authoring — 2026-05-30]: ability-score add targets that
// accept a ceiling (max) field. Only 'add' operations on the 6 core ability scores
// are eligible — 'set' targets a specific value (no ceiling concept), 'subtract'
// reduces the stat (ceiling is meaningless), and non-ability stats (ac, speed, etc.)
// have no D&D 5e score-cap convention. Fork resolution Option B per spec section 9.
const ADD_ABILITY_TARGETS = new Set([
  'addStrength', 'addDexterity', 'addConstitution',
  'addIntelligence', 'addWisdom', 'addCharisma',
]);

function StatModificationsList({
  value = [],
  onChange,
  disabled = false,
  placeholder = '-- Stat Modification --',
}) {
  const fire = (next) => onChange && onChange(next);

  const handleAdd = () => fire([...value, makeDefaultRow()]);

  const handleRemove = (id) => fire(value.filter((r) => r.id !== id));

  const updateRow = (id, patch) =>
    fire(value.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div data-testid="stat-mods-list" className="flex flex-col gap-2">
      {value.map((row) => (
        <div
          key={row.id}
          data-testid={`stat-mods-row-${row.id}`}
          className="flex items-center gap-2 p-2 bg-gray-900/40 border border-gray-700 rounded flex-wrap"
        >
          {/* Target picker — ReferencePicker renders optgroups via meta.group */}
          <span data-testid={`stat-mods-row-${row.id}-target`}>
            <ReferencePicker
              kind="statModTarget"
              value={row.target || ''}
              onChange={(next) => updateRow(row.id, { target: next })}
              disabled={disabled}
              placeholder={placeholder}
            />
          </span>

          {/* Numeric value input */}
          <input
            type="number"
            data-testid={`stat-mods-row-${row.id}-value`}
            value={row.value !== undefined ? row.value : 0}
            onChange={(e) => updateRow(row.id, { value: Number(e.target.value) })}
            disabled={disabled}
            min={row.target && row.target.startsWith('subtract') ? '1' : row.target && row.target.startsWith('set') ? '1' : '-10'}
            max={row.target && row.target.startsWith('set') ? '30' : '10'}
            placeholder={
              row.target && row.target.startsWith('set')
                ? 'Value (e.g., 19)'
                : row.target && row.target.startsWith('subtract')
                ? 'Amount (e.g., 2)'
                : 'Modifier (e.g., 2)'
            }
            className="w-24 px-2 py-1 bg-gray-800 text-white rounded border border-gray-700 focus:border-emerald-500 outline-none text-sm disabled:opacity-50"
          />

          {/* [SUB-PLAN 1H-H-maxcap-authoring — 2026-05-30]: Ability score ceiling input.
              Shown ONLY for add-ability-score targets (addStrength, addDexterity, etc.).
              Allows authoring "+2 DEX to a maximum of 20" — matching Ioun Stone semantics.
              Fork resolution Option B: gate on ability scores only (spec section 9). */}
          {ADD_ABILITY_TARGETS.has(row.target) && (
            <div className="flex items-center gap-1">
              <label
                htmlFor={`stat-mods-row-${row.id}-max`}
                className="text-xs text-amber-300 whitespace-nowrap"
              >
                max
              </label>
              <input
                type="number"
                id={`stat-mods-row-${row.id}-max`}
                data-testid={`stat-mods-row-${row.id}-max`}
                value={row.max !== undefined && row.max !== null ? row.max : ''}
                onChange={(e) =>
                  updateRow(row.id, {
                    max: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                disabled={disabled}
                min="1"
                max="30"
                placeholder="e.g. 20"
                title="Ability score ceiling (e.g. 20 for the default D&D 5e cap)"
                className="w-16 px-2 py-1 bg-gray-800 text-white rounded border border-amber-700/60 focus:border-amber-400 outline-none text-sm disabled:opacity-50"
              />
            </div>
          )}

          {/* Remove button */}
          <button
            type="button"
            data-testid={`stat-mods-row-${row.id}-remove`}
            onClick={() => handleRemove(row.id)}
            disabled={disabled}
            className="px-2 py-1 bg-red-900/40 text-red-300 border border-red-700/40 rounded text-xs hover:bg-red-900/60 disabled:opacity-50"
            aria-label="Remove stat modification"
            title="Remove"
          >
            ×
          </button>
        </div>
      ))}

      {/* Add button */}
      <button
        type="button"
        data-testid="stat-mods-add"
        onClick={handleAdd}
        disabled={disabled}
        className="px-3 py-1.5 bg-emerald-900/40 text-emerald-300 border border-emerald-700/40 rounded text-sm hover:bg-emerald-900/60 disabled:opacity-50 self-start"
      >
        + Add Stat Modification
      </button>
    </div>
  );
}

export default StatModificationsList;
